/**
 * 開催ユニット内の Arm 成績を、買い目点・レース・損益の複数軸で集計する。
 * 公開 submissions（betLineResults / outcomeCode / settledPnlJpy）のみを使う。
 */

export type KeibaPerfBetLine = 'hit' | 'miss' | 'void_refund' | 'pending';

export type KeibaPerfSubmission = {
  provider: string;
  armName: string;
  status: string;
  outcomeCode?: string;
  settledPnlJpy: number | null;
  bets?: unknown;
  betLineResults?: KeibaPerfBetLine[];
};

export type KeibaArmPerformance = {
  provider: string;
  armName: string;
  /** 精算済み提出の仮想損益合計 */
  unitPnlJpy: number;
  /** 買い目点: 的中 */
  betHits: number;
  /** 買い目点: 外れ */
  betMisses: number;
  /** 買い目点: 返還 */
  betVoids: number;
  /** 判定済み買い目点（hit+miss+void） */
  betJudged: number;
  /** 予想総数（提出した買い目点。pending 含む） */
  betTotal: number;
  /** 的中率 hit/(hit+miss)。分母0なら null */
  betHitRate: number | null;
  /** レース: 的中（旧一部的中を含む） */
  raceHits: number;
  /** 互換: 常に 0（旧一部的中は raceHits に合算） */
  raceMixed: number;
  /** レース: 外れ */
  raceMisses: number;
  /** レース: 全返還（採点対象） */
  raceVoids: number;
  /** レース: 不参加 */
  raceSkipped: number;
  /** invalid / voided（採点対象外） */
  raceExcluded: number;
  /** 参加レース（的中・外れ・返還） */
  racePlayed: number;
  /** 的中/参加。分母0なら null */
  raceHitRate: number | null;
  /** 仮想投資額（stake 合計） */
  stakeJpy: number;
  /** 仮想回収額（stake + pnl） */
  returnJpy: number;
  /** (return-stake)/stake。分母0なら null */
  roi: number | null;
  /** return / stake（回収率）。分母0なら null */
  recoveryRate: number | null;
};

function stakeFromBets(bets: unknown): number {
  if (!Array.isArray(bets)) return 0;
  let sum = 0;
  for (const raw of bets) {
    if (!raw || typeof raw !== 'object') continue;
    const stake = (raw as { stakeJpy?: unknown }).stakeJpy;
    const n = typeof stake === 'number' ? stake : Number(stake);
    if (Number.isFinite(n) && n > 0) sum += n;
  }
  return sum;
}

/** レース単位の「不参加」（UI の isEmptyBets と整合） */
export function isKeibaPerfRaceSkip(
  s: Pick<KeibaPerfSubmission, 'status' | 'outcomeCode' | 'bets'>
): boolean {
  if (s.status === 'no_submit' || s.outcomeCode === 'skip') return true;
  if (s.status === 'invalid' || s.status === 'voided') return true;
  if (s.status === 'locked' || s.status === 'submitted') return true;
  const bets = Array.isArray(s.bets) ? s.bets : [];
  if (bets.length > 0) return false;
  return s.status === 'settled';
}

function emptyArm(provider: string, armName: string): KeibaArmPerformance {
  return {
    provider,
    armName,
    unitPnlJpy: 0,
    betHits: 0,
    betMisses: 0,
    betVoids: 0,
    betJudged: 0,
    betTotal: 0,
    betHitRate: null,
    raceHits: 0,
    raceMixed: 0,
    raceMisses: 0,
    raceVoids: 0,
    raceSkipped: 0,
    raceExcluded: 0,
    racePlayed: 0,
    raceHitRate: null,
    stakeJpy: 0,
    returnJpy: 0,
    roi: null,
    recoveryRate: null,
  };
}

function finalize(a: KeibaArmPerformance): KeibaArmPerformance {
  const judgedHitMiss = a.betHits + a.betMisses;
  a.betHitRate = judgedHitMiss > 0 ? a.betHits / judgedHitMiss : null;
  a.raceHitRate =
    a.racePlayed > 0 ? (a.raceHits + a.raceMixed) / a.racePlayed : null;
  a.returnJpy = a.stakeJpy + a.unitPnlJpy;
  a.roi = a.stakeJpy > 0 ? a.unitPnlJpy / a.stakeJpy : null;
  a.recoveryRate = a.stakeJpy > 0 ? a.returnJpy / a.stakeJpy : null;
  return a;
}

/**
 * Arm ごとの成績。`arms` を渡すとその順・その集合で空枠も含めて返す。
 */
export function computeKeibaArmPerformanceStats(
  submissions: KeibaPerfSubmission[],
  arms?: Array<{ provider: string; name: string }>,
  opts?: { imputeMissingRaceAsSkip?: boolean }
): KeibaArmPerformance[] {
  const byKey = new Map<string, KeibaArmPerformance>();
  const touchedProviders = new Set<string>();

  const ensure = (provider: string, armName: string) => {
    const key = `${provider}::${armName}`;
    let row = byKey.get(key);
    if (!row) {
      row = emptyArm(provider, armName);
      byKey.set(key, row);
    }
    return row;
  };

  if (arms) {
    for (const a of arms) ensure(a.provider, a.name);
  }

  for (const s of submissions) {
    touchedProviders.add(s.provider);
    const row = ensure(s.provider, s.armName);
    const stake = stakeFromBets(s.bets);
    const lines = Array.isArray(s.betLineResults) ? s.betLineResults : [];
    const betCount = Array.isArray(s.bets) ? s.bets.length : lines.length;

    if (s.status === 'invalid' || s.status === 'voided') {
      row.raceExcluded += 1;
      continue;
    }
    if (isKeibaPerfRaceSkip(s)) {
      row.raceSkipped += 1;
      continue;
    }

    if (s.status !== 'settled') {
      row.raceSkipped += 1;
      continue;
    }

    if (s.settledPnlJpy != null) {
      row.unitPnlJpy += s.settledPnlJpy;
      row.stakeJpy += stake;
    }

    if (betCount > 0) {
      row.betTotal += betCount;
      if (lines.length === betCount) {
        for (const line of lines) {
          if (line === 'hit') {
            row.betHits += 1;
            row.betJudged += 1;
          } else if (line === 'miss') {
            row.betMisses += 1;
            row.betJudged += 1;
          } else if (line === 'void_refund') {
            row.betVoids += 1;
            row.betJudged += 1;
          }
        }
      } else if (s.status === 'settled' && Array.isArray(s.bets)) {
        // betLineResults 欠落時は点数だけ計上（判定は outcome に委ねる）
        row.betTotal = Math.max(row.betTotal, betCount);
      }
    }

    const code = s.outcomeCode || '';
    if (code === 'hit' || code === 'mixed') {
      row.raceHits += 1;
      row.racePlayed += 1;
    } else if (code === 'miss') {
      row.raceMisses += 1;
      row.racePlayed += 1;
    } else if (code === 'void') {
      row.raceVoids += 1;
      row.racePlayed += 1;
    }
  }

  if (opts?.imputeMissingRaceAsSkip && arms && touchedProviders.size > 0) {
    for (const a of arms) {
      if (!touchedProviders.has(a.provider)) {
        ensure(a.provider, a.name).raceSkipped += 1;
      }
    }
  }

  const list = [...byKey.values()].map(finalize);
  if (arms) {
    const order = new Map(arms.map((a, i) => [`${a.provider}::${a.name}`, i]));
    list.sort(
      (a, b) =>
        (order.get(`${a.provider}::${a.armName}`) ?? 99) -
        (order.get(`${b.provider}::${b.armName}`) ?? 99)
    );
  }
  return list;
}

export function formatKeibaRatePct(rate: number | null, digits = 0): string {
  if (rate == null || !Number.isFinite(rate)) return '—';
  return `${(rate * 100).toFixed(digits)}%`;
}

/** 公開 API `/performance` の行を UI 用型へ */
export function mapKeibaCareerPerformanceRow(row: {
  provider: string;
  armName: string;
  unitPnlJpy: number;
  betHits: number;
  betMisses: number;
  betVoids: number;
  betTotal: number;
  betHitRate: number | null;
  raceHits: number;
  raceMixed: number;
  raceMisses: number;
  raceVoids?: number;
  raceSkipped: number;
  raceExcluded?: number;
  racePlayed: number;
  raceHitRate: number | null;
  stakeJpy: number;
  returnJpy: number;
  roi: number | null;
  recoveryRate?: number | null;
}): KeibaArmPerformance {
  return {
    provider: row.provider,
    armName: row.armName,
    unitPnlJpy: row.unitPnlJpy,
    betHits: row.betHits,
    betMisses: row.betMisses,
    betVoids: row.betVoids,
    betJudged: row.betHits + row.betMisses + row.betVoids,
    betTotal: row.betTotal,
    betHitRate: row.betHitRate,
    raceHits: row.raceHits,
    raceMixed: row.raceMixed,
    raceMisses: row.raceMisses,
    raceVoids: row.raceVoids ?? 0,
    raceSkipped: row.raceSkipped,
    raceExcluded: row.raceExcluded ?? 0,
    racePlayed: row.racePlayed,
    raceHitRate: row.raceHitRate,
    stakeJpy: row.stakeJpy,
    returnJpy: row.returnJpy,
    roi: row.roi,
    recoveryRate: row.recoveryRate ?? (row.stakeJpy > 0 ? row.returnJpy / row.stakeJpy : null),
  };
}
